import type { RecordType } from '@/hooks/useCommandPaletteItems';

const RECENT_KEY_PREFIX = 'command-palette-recent:';
const RECENT_LIMIT = 5;

type RecentType = 'page' | 'setting' | RecordType;

/**
 * One recently opened palette target. A page or setting keeps its path as
 * `id` and re-resolves its label in the current locale; a record keeps its
 * id and the name it showed, so the list needs no request to render.
 */
export interface RecentEntry {
  type: RecentType;
  id: string;
  name?: string;
}

const RECENT_TYPES: readonly RecentType[] = ['page', 'setting', 'child', 'contact', 'employee'];

export function recentStorageKey(orgId: string | null | undefined, userId: string | null | undefined): string | null {
  return orgId && userId ? `${RECENT_KEY_PREFIX}${orgId}:${userId}` : null;
}

/** The palette key of an entry, the same as the matching item's id (`page:/children`, `child:<id>`). */
export function recentEntryKey(entry: RecentEntry): string {
  return `${entry.type}:${entry.id}`;
}

function isRecentEntry(value: unknown): value is RecentEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<RecentEntry>;
  return (
    RECENT_TYPES.includes(entry.type as RecentType) &&
    typeof entry.id === 'string' &&
    (entry.name === undefined || typeof entry.name === 'string')
  );
}

export function readRecentEntries(key: string | null): RecentEntry[] {
  if (!key) return [];
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isRecentEntry) : [];
  } catch {
    return [];
  }
}

/** Puts `entry` first, drops its older copy, keeps the newest `RECENT_LIMIT`; returns the new list. */
export function writeRecentEntry(key: string | null, entry: RecentEntry): RecentEntry[] {
  const next = [
    entry,
    ...readRecentEntries(key).filter((existing) => recentEntryKey(existing) !== recentEntryKey(entry)),
  ].slice(0, RECENT_LIMIT);
  if (key) {
    try {
      sessionStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Storage full or blocked: the list still works until the palette closes.
    }
  }
  return next;
}

/**
 * Removes every user's palette history from this tab. Recent records carry
 * names of children and parents, and Kita PCs are shared, so sign-out wipes
 * them; sessionStorage already drops them when the tab closes.
 */
export function clearRecentPaletteItems(): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(RECENT_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing was written either.
  }
}
